import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { useState } from 'react';
import { BlogBlueprintsRunPayload } from '../../types/blog-generation-agent.types';
import { createBlogPostBlueprints } from 'src/common/Scraper';
import useUpdateUserAgents from '../../hooks/useUpdateUserAgents';
import { sleep } from 'src/common/config/FunctionHelper';

/**
 * A custom hook to manage blog blueprint creation mutation state and handlers.
 * 
 * @param userId - The user ID.
 * @param siteId - The site ID.
 * @returns An object containing the mutation state, pending status, and execution handler.
 */

interface UseBlogGenerationMutationProps {
  topics?: string[];
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onMutateStart?: () => void;
  siteIdOverride?: string;
  userIdOverride?: string;
}

function useBlogGenerationMutation({ onSuccess, onError, onMutateStart, topics: initialTopics = [], siteIdOverride, userIdOverride }: UseBlogGenerationMutationProps = {}) {
  const queryClient = useQueryClient();
  const { mutate: updateAgents } = useUpdateUserAgents('blog-generation');

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [topics, setTopics] = useState<string[]>(initialTopics);

  const { _id: reduxSiteId, user_id: reduxUserId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  );
  const siteId = siteIdOverride || reduxSiteId;
  const userId = userIdOverride || reduxUserId;

  const blogBlueprintsMutation = useMutation({
    mutationFn: (payload: BlogBlueprintsRunPayload) => createBlogPostBlueprints(payload),
    mutationKey: ['create-blog-blueprint', userId, siteId],
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['blog-blueprints', userId, siteId]
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error, variables) => {
      if (onError) {
        onError(error);
      }
      handleAgentStatusUpdate(false, variables?.site_id, variables?.topics)
    }
  })

  const pendingMutations = useMutationState({
    filters: { mutationKey: ['create-blog-blueprint', userId, siteId], status: 'pending' },
  });

  const isMutationPending = pendingMutations.length > 0;

  function handleAgentStatusUpdate(status: boolean, overrideSiteId?: string, topicKeywords?: string[]){
    updateAgents({
      site_id: overrideSiteId || siteId,
      agents: [
        {
          name: 'blog_generation',
          enabled: status,
          config: {
            topics: status ? (topicKeywords || topics) : [],
            keywords: ['']
          }
        }
      ]
    });
  }

  const handleEnableBlogGeneration = async (kws: string[], overrides: { siteId: string; userId: string, shouldRun?: boolean }) => {
    const effectiveSiteId = overrides?.siteId || siteId;
    const effectiveUserId = overrides?.userId || userId;
    const shouldRun = overrides?.shouldRun ?? true;

    if (onMutateStart) {
      onMutateStart();
    }

    setTimeout(() => {
      setDialogOpen(false);
    }, 2000);

    handleAgentStatusUpdate(true, overrides?.siteId, kws)

    await sleep(1000);

    if(shouldRun){
      blogBlueprintsMutation.mutate({
        topics: kws,
        user_id: effectiveUserId,
        site_id: effectiveSiteId,
      });
    }

    // await sleep(2000);

    // handleAgentStatusUpdate(true, overrides?.siteId, kws)

    // setTimeout(() => {
    //   queryClient.invalidateQueries({
    //     queryKey: ['agentsStatus', siteId]
    //   });
    // }, 2000);

  };

  return {
    dialogOpen,
    setDialogOpen,
    topics,
    setTopics,
    blogBlueprintsMutation,
    isMutationPending,
    handleEnableBlogGeneration,
    updateBlogAgentStatus: handleAgentStatusUpdate
  };
}

export default useBlogGenerationMutation;
